import { repository } from '../../data'
import { EmptyState } from '../primitives'
import type { ColKind, RowKind, View } from './derive'
import { cell } from './derive'
import { Avatar, NetDot, useArm, useWrite } from './parts'

const KIND_NOTE = { all_robots: 'every robot', all_computers: 'every office PC', custom: 'hand-picked' } as const
const NET_NOTE = { online: 'online', offline: 'offline', not_joined: 'not joined' } as const
const shortDay = (iso: string): string => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
/** Where inherited access comes from, as short as it goes: "Autonomy", "All office computers". */
const source = (reason: string): string => {
  const [who, what = ''] = reason.replace(/^via /, '').split(' → ')
  return (who === 'Directly' ? what : who ?? '').split(' · ')[0] ?? ''
}

/**
 * Who can SSH into what. Rows are people or teams, columns machines or
 * groups — People × Machines for a small office, Teams × Groups at two hundred
 * robots. A filled cell is a rule made right there; a dashed one is access
 * arriving through a team or a group, shown so the grid never claims someone
 * is out when they are in.
 */
export function RulesMatrix({
  view,
  rows,
  cols,
  onRows,
  onCols,
  onTeam,
  onGroup,
}: {
  view: View
  rows: RowKind
  cols: ColKind
  onRows: (k: RowKind) => void
  onCols: (k: ColKind) => void
  onTeam: (id: string | null) => void
  onGroup: (id: string | null) => void
}): JSX.Element {
  const arm = useArm()
  const rowItems =
    rows === 'people'
      ? view.people.map((p) => ({ id: p.id, name: p.name, sub: p.online ? 'online' : '', initials: p.initials, online: p.online }))
      : view.teams.map((t) => ({ id: t.id, name: t.name, sub: `${t.memberIds.length} ${t.memberIds.length === 1 ? 'person' : 'people'}`, initials: null, online: false }))
  const colItems =
    cols === 'machines'
      ? [...view.machines]
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
          .map((m) => ({ id: m.id, name: m.name, sub: NET_NOTE[m.net], net: m.net }))
      : view.groups.map((g) => ({ id: g.id, name: g.name, sub: `${g.machineIds.length} · ${KIND_NOTE[g.kind]}`, net: null }))
  const who = (id: string) => (rows === 'people' ? { userId: id } : { teamId: id })
  const what = (id: string) => (cols === 'machines' ? { machineId: id } : { groupId: id })
  const rule = useWrite(
    (rowId: string, colId: string, on: boolean) => repository.setRule({ ...who(rowId), ...what(colId), level: on ? 'full' : null }),
    (rowId, colId, on) => {
      const r = rowItems.find((x) => x.id === rowId)?.name
      const c = colItems.find((x) => x.id === colId)?.name
      return on ? `${r} can now SSH into ${c}` : `${r} can no longer SSH into ${c} · open sessions closed`
    },
  )
  const template = `minmax(200px, 240px) repeat(${colItems.length}, minmax(96px, 120px))`

  return (
    <section className="cx-panel" aria-label="Who can SSH into what">
      <header className="cx-panel__head">
        <div className="stack" style={{ gap: 2 }}>
          <h2 className="cx-panel__title">Who can SSH into what</h2>
          <span className="muted cx-panel__hint">Press a cell to let them in; press it twice to take it away.</span>
        </div>
        <div className="cx-modes">
          <div className="segmented" role="tablist" aria-label="Rows">
            <button role="tab" aria-selected={rows === 'people'} onClick={() => onRows('people')}>People</button>
            <button role="tab" aria-selected={rows === 'teams'} onClick={() => onRows('teams')}>Teams</button>
          </div>
          <span className="cx-modes__by" aria-hidden="true">×</span>
          <div className="segmented" role="tablist" aria-label="Columns">
            <button role="tab" aria-selected={cols === 'machines'} onClick={() => onCols('machines')}>Machines</button>
            <button role="tab" aria-selected={cols === 'groups'} onClick={() => onCols('groups')}>Groups</button>
          </div>
          {rows === 'teams' ? <button onClick={() => onTeam(null)}>New team</button> : null}
          {cols === 'groups' ? <button onClick={() => onGroup(null)}>New group</button> : null}
        </div>
      </header>

      {rowItems.length === 0 || colItems.length === 0 ? (
        <EmptyState title={rowItems.length === 0 ? (rows === 'teams' ? 'No teams yet' : 'Nobody listed yet') : cols === 'machines' ? 'No machines yet' : 'No groups yet'}>
          {rowItems.length === 0 && rows === 'teams'
            ? 'Teams are for when many people need the same machines. With a small team, show People.'
            : 'Add machines from the Machines tab — the ones already on the network are listed there.'}
        </EmptyState>
      ) : (
        <div className="cx-grid" role="grid" aria-label={`${rows} and ${cols}`}>
          <div className="cx-grid__row cx-grid__row--head" role="row" style={{ gridTemplateColumns: template }}>
            <span className="cx-grid__corner" role="columnheader">{rows === 'people' ? 'Person' : 'Team'}</span>
            {colItems.map((c) => (
              <button key={c.id} className="cx-grid__col" role="columnheader" onClick={() => (cols === 'groups' ? onGroup(c.id) : undefined)} tabIndex={cols === 'groups' ? 0 : -1}>
                <span className="cx-grid__col-name">
                  {c.net ? <NetDot state={c.net} /> : null}
                  <span>{c.name}</span>
                </span>
                <span className="cx-grid__col-sub">{c.sub}</span>
              </button>
            ))}
          </div>
          {rowItems.map((r) => (
            <div key={r.id} className="cx-grid__row" role="row" style={{ gridTemplateColumns: template }}>
              <button className="cx-grid__rowhead" role="rowheader" onClick={() => (rows === 'teams' ? onTeam(r.id) : undefined)} tabIndex={rows === 'teams' ? 0 : -1}>
                {r.initials ? <Avatar initials={r.initials} online={r.online} size={30} /> : <span className="cx-grid__team-mark" aria-hidden="true" />}
                <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                  <span className="cx-grid__row-name">{r.name}</span>
                  {r.sub ? <span className="cx-grid__row-sub">{r.sub}</span> : null}
                </span>
              </button>
              {colItems.map((c) => {
                const { exact, inherited } = cell(view, rows, r.id, cols, c.id)
                const key = `${r.id}|${c.id}`
                const armed = arm.armed(key)
                const state = armed ? 'armed' : exact ? (exact.level === 'restricted' ? 'restricted' : 'on') : inherited.length ? 'inherited' : 'off'
                const caption = armed
                  ? 'Sure?'
                  : exact?.expiresAt
                    ? shortDay(exact.expiresAt)
                    : !exact && inherited.length
                      ? source(inherited[0] as string)
                      : ''
                const label = `${r.name} on ${c.name}: ${exact ? 'can SSH in. Press twice to take it away.' : inherited.length ? `can SSH in, ${inherited.join(', ')}.` : 'no access. Press to let them in.'}`
                return (
                  <span key={c.id} className="cx-grid__cell" role="gridcell" title={inherited.join('\n') || undefined}>
                    <button
                      className={`cx-toggle is-${state} ${exact?.expiresAt && !armed ? 'is-ending' : ''}`}
                      aria-label={label}
                      aria-pressed={!!exact}
                      disabled={rule.pending}
                      onClick={() => (exact ? arm.press(key, () => rule.run(r.id, c.id, false)) : rule.run(r.id, c.id, true))}
                    >
                      {state === 'armed' ? <Cross /> : state === 'off' ? <Plus /> : <Check />}
                      {state === 'restricted' ? <span className="cx-toggle__badge">R</span> : null}
                    </button>
                    <span className={`cx-grid__caption ${armed ? 'is-armed' : ''}`}>{caption}</span>
                  </span>
                )
              })}
            </div>
          ))}
        </div>
      )}

      <footer className="cx-legend" aria-label="Key">
        <span><span className="cx-toggle is-on is-key"><Check /></span> Can SSH in</span>
        <span><span className="cx-toggle is-restricted is-key"><Check /></span> Restricted</span>
        <span><span className="cx-toggle is-inherited is-key"><Check /></span> Through a team or group</span>
        <span><span className="cx-toggle is-on is-ending is-key"><Check /></span> Ends on a date</span>
      </footer>
    </section>
  )
}

export function Check(): JSX.Element {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  )
}

function Plus(): JSX.Element {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M12 6v12M6 12h12" />
    </svg>
  )
}

function Cross(): JSX.Element {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M7 7l10 10M17 7L7 17" />
    </svg>
  )
}
